import React, { useEffect, useState } from 'react';
import { DRAFT_CHANGED_EVENT, DraftScope, draftKey, readDraft } from '../editorDraft';
import { validQuoteDraft } from '../quoteDraft';
import { Quote } from '../types';

export function QuoteDraftRecovery({ scope, quotes, onOpen }: {
  scope: DraftScope; quotes: Quote[]; onOpen: (quote: Quote | null) => void;
}) {
  const [drafts, setDrafts] = useState<Array<{ id: string; title: string; unsavedQuote: Quote | null }>>([]);
  const prefix = draftKey(scope).slice(0, -3);
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    const refresh = () => setGeneration(n => n + 1);
    window.addEventListener(DRAFT_CHANGED_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(DRAFT_CHANGED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  useEffect(() => {
    try {
      const found = [];
      for (let index = 0; index < sessionStorage.length; index++) {
        const key = sessionStorage.key(index);
        if (!key?.startsWith(prefix)) continue;
        try {
          const entry = readDraft(key, validQuoteDraft);
          if (entry) {
            const id = decodeURIComponent(key.slice(prefix.length));
            const original = !entry.value.persistedQuote && id !== 'new' ? JSON.parse(entry.baseline) : null;
            const unsavedQuote = original?.id === id && Array.isArray(original.items) && original.client ? original as Quote : null;
            found.push({ id, title: entry.value.title || 'Untitled Quote', unsavedQuote });
          }
        } catch { /* The editor reports incompatible storage without logging its contents. */ }
      }
      setDrafts(found);
    } catch { setDrafts([]); }
  }, [prefix, generation]);
  const review = (id: string, quote: Quote | null) => {
    // Never trust a previously rendered list entry after another Save/Discard.
    try {
      if (readDraft(`${prefix}${encodeURIComponent(id)}`, validQuoteDraft)) onOpen(quote);
    } catch {
      // Incompatible/replaced entries are never opened from an obsolete list row.
    } finally { setGeneration(n => n + 1); }
  };
  if (!drafts.length) return null;
  return <section aria-label="Recoverable Quote drafts" className="p-4 mb-4 border border-amber-500 rounded-xl text-white">
    <h2>Unsaved Quote drafts in this tab</h2>
    <p>Nothing is saved to the database until you explicitly Save. Open a draft to choose Resume or Discard.</p>
    {drafts.map(draft => {
      const saved = quotes.find(quote => quote.id === draft.id);
      return <div key={draft.id} className="py-2">{draft.title} — {draft.id === 'new' || saved || draft.unsavedQuote
        ? <button className="underline p-3" onClick={() => review(draft.id, saved || draft.unsavedQuote || null)}>Review unsaved draft</button>
        : <span>Saved Quote is not currently available. Draft retained; it will not recreate a missing record automatically.</span>}</div>;
    })}
  </section>;
}
